/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';

import { GroupTypeTransformerMapping } from '@bingads-webui-campaign-react/ad-placement-connector';
import { GroupTypeTemplatesMapping, templateParserUtils } from '@bingads-webui-campaign-react/ad-placement-templates';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SinglePlacement, PreviewCarouselLayout, GroupsPreviewer } from '@bingads-webui-campaign-react/ad-placement-layouts';
import { getAdPlacementElement } from '@bingads-webui-campaign-react/ad-placement-view';
import { getGenericImageObjectAsync, getAutoCroppedImagesForRatios } from '@bingads-webui-campaign/image-experience-utils';

import defaultImage from '../media/191to1.jpg';

const {
  getImageTypeForTemplate,
  getCarouselTitleForTemplate,
  getMinSizeForTemplate,
  validateImageSizeForTemplate,
  getCropSettingsInfoFromTemplates,
  getIsRequiredFromTemplate,
  getTemplateTypeForTemplate,
  doesTemplateShowImage,
  doesTemplateHideHeaderButtons,
} = templateParserUtils;

const doAnyTemplatesHaveImages = (templates) => {
  const aspectRatios = _.filter(getCropSettingsInfoFromTemplates(templates), ({ aspectRatio }) => aspectRatio);

  return aspectRatios.length > 0;
};

const getGenericImagesForTemplateAsync = (templates, defaultImageToUse = defaultImage) => {
  const aspectRatios = _.filter(getCropSettingsInfoFromTemplates(templates), ({ aspectRatio }) => aspectRatio);
  const setupPromise = getGenericImageObjectAsync(defaultImageToUse)
    .then((imageObject) => {
      const croppedImages = getAutoCroppedImagesForRatios({
        originalImageToCrop: imageObject,
        aspectRatios,
      });

      _.each(croppedImages, (image) => {
        // eslint-disable-next-line no-param-reassign
        image.isIncluded = false;
        // eslint-disable-next-line no-param-reassign
        image.isRequired = false;
      });

      return croppedImages;
    });

  return setupPromise;
};

export const getFilteredTemplates = ({
  groupType,
  placementFilterPredicate,
  placementCustomizationPredicate,
}) => {
  const groupTypeTemplates = GroupTypeTemplatesMapping[groupType];

  let templatesToUse = placementFilterPredicate ? _.filter(groupTypeTemplates, template => placementFilterPredicate(template)) : groupTypeTemplates;

  if (placementCustomizationPredicate) {
    templatesToUse = _.map(templatesToUse, template => placementCustomizationPredicate(template));
  }

  return templatesToUse;
};

export const getSinglePlacementLayouts = ({ groupType, placementFilterPredicate, placementCustomizationPredicate }) => {
  const templatesToUse = getFilteredTemplates({ groupType, placementFilterPredicate, placementCustomizationPredicate });
  const templateTransformerFunc = GroupTypeTransformerMapping[groupType];

  // eslint-disable-next-line arrow-body-style
  return _.map(templatesToUse, (template) => {
    return {
      template,
      // eslint-disable-next-line react/prop-types
      SinglePlacementLayout: ({ adData, images, decoratorsData }) => {
        const i18n = useContext(I18nContext);
        const { AdPlacementElement } = useMemo(() => getAdPlacementElement({
          template,
          templateTransformerFunc,
          i18n,
        }), [i18n]);

        // eslint-disable-next-line react/prop-types
        return (
          <SinglePlacement
            onRenderPlacement={() => (
              <AdPlacementElement
                adData={adData}
                imagesToDisplay={images}
                decoratorsData={decoratorsData}
              />
            )}
          />
        );
      },
    };
  });
};

export const useSinglePlacementLayouts = ({ groupType, placementFilterPredicate, placementCustomizationPredicate }) => {
  const layouts = useMemo(() => getSinglePlacementLayouts({ groupType, placementFilterPredicate, placementCustomizationPredicate }), [
    groupType,
    placementFilterPredicate,
    placementCustomizationPredicate,
  ]);

  return layouts;
};


export const getPreviewCarouselLayout = ({
  groupType,
  placementFilterPredicate,
  placementCustomizationPredicate,
  customDefaultImage,
}) => {
  const templateTransformerFunc = GroupTypeTransformerMapping[groupType];
  const templatesToUse = getFilteredTemplates({ groupType, placementFilterPredicate, placementCustomizationPredicate });

  const PreviewCarouselLayoutComponent = (props) => {
    const {
      adData,
      imagesToDisplay,
      logo,
      decoratorsData,
      headerButtonProps,
      getIsRequiredByIndex,
      getCarouselTitleByIndex,
      hideHeaderButtons,
      hideNavigationControlsIfSingle,
    } = props;
    const [displayImages, setDisplayImages] = useState([]);

    const placeholderImagesRef = useRef(null);
    const isDisplayingPlaceholderImages = useRef(false);

    const i18n = useContext(I18nContext);

    const isImageListEmpty = useMemo(() => imagesToDisplay.length === 0, [imagesToDisplay]);

    const anyImageTemplates = useMemo(() => doAnyTemplatesHaveImages(templatesToUse), [templatesToUse]);

    useEffect(() => {
      if (anyImageTemplates) {
        if (isImageListEmpty) {
          // no need to regenerate placeholder images if present
          if (!placeholderImagesRef.current) {
            getGenericImagesForTemplateAsync(templatesToUse, customDefaultImage)
              .then((placeholderImages) => {
                placeholderImagesRef.current = placeholderImages;
                setDisplayImages([
                  ...placeholderImages,
                ]);
                isDisplayingPlaceholderImages.current = true;
              });
          } else if (!isDisplayingPlaceholderImages.current) {
            setDisplayImages([
              ...placeholderImagesRef.current,
            ]);
            isDisplayingPlaceholderImages.current = true;
          }
        } else {
          setDisplayImages([
            ...imagesToDisplay,
          ]);
          isDisplayingPlaceholderImages.current = false;
        }
      }
    }, [imagesToDisplay, isImageListEmpty, anyImageTemplates]);

    const placementComponents = useMemo(() => {
      const componentList = _.map(templatesToUse, (template) => {
        const { AdPlacementElement } = getAdPlacementElement({
          template,
          templateTransformerFunc,
          i18n,
        });

        return AdPlacementElement;
      });

      return componentList;
    }, [i18n, displayImages]);

    const previewElements = useMemo(() => {
      if (displayImages.length > 0 || !anyImageTemplates) {
        // eslint-disable-next-line arrow-body-style
        const elementList = _.map(placementComponents, (AdPlacementElement) => {
          return (
            <AdPlacementElement
              adData={adData}
              imagesToDisplay={displayImages}
              decoratorsData={decoratorsData}
              logo={logo}
            />
          );
        });

        return elementList;
      }
      return [];
    }, [adData, displayImages, decoratorsData, placementComponents]);

    const getImageInfoByIndex = useCallback((index) => {
      const imageType = getImageTypeForTemplate(templatesToUse[index]);

      return _.find(displayImages, ({ associationType }) => associationType === imageType);
    }, [displayImages]);

    const getCarouselTitleByIndexImpl = useCallback((index) => {
      const title = getCarouselTitleByIndex ? getCarouselTitleByIndex(index) : getCarouselTitleForTemplate(templatesToUse[index], groupType, i18n);

      return title;
    }, [getCarouselTitleByIndex]);

    const getMinSizeByIndex = ((index) => {
      const template = templatesToUse[index];

      return getMinSizeForTemplate(template);
    });

    const validateImageSizeByIndex = useCallback((index) => {
      if (isImageListEmpty) {
        return true;
      }

      const imageInfo = getImageInfoByIndex(index);

      return validateImageSizeForTemplate(templatesToUse[index], imageInfo);
    }, [getImageInfoByIndex, isImageListEmpty]);

    const getIsRequiredByIndexImpl = useCallback((index) => {
      const isRequired = getIsRequiredByIndex ? getIsRequiredByIndex(index, templatesToUse[index]) : getIsRequiredFromTemplate(templatesToUse[index]);

      return isRequired;
    }, [getIsRequiredByIndex]);

    const getTemplateTypeByIndex = useCallback((index) => {
      const type = getTemplateTypeForTemplate(templatesToUse[index]);

      return type;
    }, []);

    const headerButtonIncludeMethodWrapper = useCallback((type, originalFunc, newValue) => {
      // eslint-disable-next-line arrow-body-style
      const index = _.findIndex(templatesToUse, (template) => {
        return getTemplateTypeForTemplate(template) === type;
      });
      const imageType = getImageTypeForTemplate(templatesToUse[index]);

      setDisplayImages((prevImages) => {
        const imageIndex = _.findIndex(prevImages, ({ associationType }) => associationType === imageType);

        // eslint-disable-next-line no-param-reassign
        prevImages[imageIndex].isIncluded = newValue;

        return [
          ...prevImages,
        ];
      });

      originalFunc(type);
    }, []);

    const updatedHeaderButtonProps = useMemo(() => {
      if (headerButtonProps && headerButtonProps.onAddAspectRatio) {
        const updatedProps = {
          onEdit: headerButtonProps.onEdit,
        };

        updatedProps.onAddAspectRatio = (type) => {
          headerButtonIncludeMethodWrapper(type, headerButtonProps.onAddAspectRatio, true);
        };

        updatedProps.onRemoveAspectRatio = (type) => {
          headerButtonIncludeMethodWrapper(type, headerButtonProps.onRemoveAspectRatio, false);
        };

        return updatedProps;
      }
      return headerButtonProps;
    }, [headerButtonIncludeMethodWrapper, headerButtonProps]);

    const doesTemplateShowImageByIndex = useCallback((index) => {
      const template = templatesToUse[index];

      return doesTemplateShowImage(template);
    }, []);

    const doesTemplateHideHeaderButtonsByIndex = useCallback((index) => {
      const template = templatesToUse[index];

      return doesTemplateHideHeaderButtons(template);
    }, []);

    // Hide header buttons if template explicitly hides them or if template does not show an image
    const shouldHideHeaderButtonsByIndex = useCallback(
      index => doesTemplateHideHeaderButtonsByIndex(index) || !doesTemplateShowImageByIndex(index)
      , [doesTemplateHideHeaderButtonsByIndex, doesTemplateShowImageByIndex]
    );

    return (
      <PreviewCarouselLayout
        {...props}
        previewElements={previewElements}
        getImageInfoByIndex={getImageInfoByIndex}
        getCarouselTitleByIndex={getCarouselTitleByIndexImpl}
        getMinSizeByIndex={getMinSizeByIndex}
        validateImageSizeByIndex={validateImageSizeByIndex}
        getTemplateTypeByIndex={getTemplateTypeByIndex}
        getIsRequiredByIndex={getIsRequiredByIndexImpl}
        hideHeaderButtons={hideHeaderButtons || isImageListEmpty}
        headerButtonProps={updatedHeaderButtonProps}
        shouldHideHeaderButtonsByIndex={shouldHideHeaderButtonsByIndex}
        doesTemplateShowImageByIndex={doesTemplateShowImageByIndex}
        hideNavigationControlsIfSingle={hideNavigationControlsIfSingle}
      />
    );
  };

  PreviewCarouselLayoutComponent.propTypes = {
    adData: PropTypes.shape({}).isRequired,
    headerButtonProps: PropTypes.shape({
      onEdit: PropTypes.func,
      onAddAspectRatio: PropTypes.func,
      onRemoveAspectRatio: PropTypes.func,
    }),
    imagesToDisplay: PropTypes.arrayOf(PropTypes.shape({})),
    decoratorsData: PropTypes.shape({}),
    getIsRequiredByIndex: PropTypes.func,
    getCarouselTitleByIndex: PropTypes.func,
    hideHeaderButtons: PropTypes.bool,
    hideNavigationControlsIfSingle: PropTypes.bool,
    logo: PropTypes.shape({}),
  };

  PreviewCarouselLayoutComponent.defaultProps = {
    headerButtonProps: null,
    imagesToDisplay: [],
    decoratorsData: {},
    getIsRequiredByIndex: null,
    getCarouselTitleByIndex: null,
    hideHeaderButtons: null,
    hideNavigationControlsIfSingle: false,
    logo: null,
  };

  return {
    PreviewCarouselLayout: PreviewCarouselLayoutComponent,
  };
};

export const usePreviewCarouselLayout = ({
  groupType,
  placementFilterPredicate,
  placementCustomizationPredicate,
  customDefaultImage,
}) => {
  const layout = useMemo(() => getPreviewCarouselLayout({
    groupType,
    placementFilterPredicate,
    placementCustomizationPredicate,
    customDefaultImage,
  }), [groupType, placementFilterPredicate, placementCustomizationPredicate, customDefaultImage]);

  return layout;
};

export const getGroupsPreviewerLayout = ({
  groupsDefinitions,
  customDefaultImage,
}) => {
  const previewers = _.chain(groupsDefinitions)
    .map(({
      key,
      groupType,
      placementFilterPredicate,
      placementCustomizationPredicate,
    }) => {
      const { PreviewCarouselLayout: PreviewerLayout } =
        getPreviewCarouselLayout({
          groupType,
          placementFilterPredicate,
          customDefaultImage,
          placementCustomizationPredicate,
        });

      return [key, PreviewerLayout];
    })
    .object()
    .value();

  const GroupsPreviewerComponent = props => (
    <GroupsPreviewer
      groups={groupsDefinitions}
      previewers={previewers}
      {...props}
    />
  );

  GroupsPreviewerComponent.propTypes = {
    adData: PropTypes.shape({}).isRequired,
    decoratorsData: PropTypes.shape({}),
    images: PropTypes.arrayOf(PropTypes.shape({})),
    headerButtonProps: PropTypes.shape({}).isRequired,
  };

  GroupsPreviewerComponent.defaultProps = {
    decoratorsData: {},
    images: [],
  };

  return {
    GroupsPreviewer: GroupsPreviewerComponent,
  };
};

export const useGroupsPreviewerLayout = ({
  groupsDefinitions,
  customDefaultImage,
}) => {
  const layout = useMemo(() => getGroupsPreviewerLayout({
    groupsDefinitions,
    customDefaultImage,
  }), [groupsDefinitions, customDefaultImage]);

  return layout;
};
